import React from 'react';
import { css, StyleSheet } from 'aphrodite/no-important';
import { CSSTransition } from 'react-transition-group';
import { ChevronIcon, Directions } from '../../../ChevronIcon';
import {
  chevronIconEnterDelay,
  chevronIconEnterDuration,
  chevronIconEnterTimeOut,
  chevronIconExitDuration,
  chevronIconExitTimeOut,
} from './menuLinks.utils';
import { generateLinkStyleFromTheme, TextLinkComponent } from '../../../../../../partials/link/TextLink';
import { useTheme } from '../../../../../../../theming/useTheme';
import { MEDIA_BREAKPOINTS } from '../../../../../../../mediaQueries.const';
import { H4 } from '../../../../../../../typography/semanticTags';
import { BaseUnit } from '../../../../../../../theming/baseUnitDefinition';
import { TranslatedFontSize } from '../../../../../../../theming/fontSizeTranslator';
import { MenuSubLinks } from './MenuSubLinks';
import { MenuLinkDataProps } from './MenuSubLink';

export const componentConfigThemeKey = 'menuLink';

export interface MenuLinkThemeProps {
  baseUnit: BaseUnit;
  idleColor: string;
  activeStateColor: string;
  fontSizesStyles: TranslatedFontSize;
}

interface MenuLinkLogicProps {
  index: number;
  onClick: (index: number) => void;
  isLinkOpen: boolean;
  startChevronIconAnimation: boolean;
}

export const getMenuLinkStyle = (baseUnit: BaseUnit, linkColor: string) => StyleSheet.create({
  style: {
    color: linkColor,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    [MEDIA_BREAKPOINTS.medium]: {
      margin: `${baseUnit}px`,
    },
    [MEDIA_BREAKPOINTS.small]: {
      margin: `${baseUnit}px`,
    },
  },
  enter: {
    opacity: 0,
  },
  enterActive: {
    opacity: 1,
    transition: `opacity ${chevronIconEnterDuration}ms ease-out`,
    transitionDelay: `${chevronIconEnterDelay}ms`,
  },
  exit: {
    opacity: 1,
  },
  exitActive: {
    opacity: 0,
    transition: `opacity ${chevronIconExitDuration}ms ease-in`,
  },
  chevronIcon: {
    width: '100%',
    display: 'flex',
    flexDirection: 'row-reverse',
  },
});

export const MenuLink: React.FunctionComponent<MenuLinkDataProps & MenuLinkLogicProps> = props => {
  const { idleColor, activeStateColor, baseUnit } = useTheme<MenuLinkThemeProps>(componentConfigThemeKey);
  const { link, onClick, index, isLinkOpen, childrenLinks, startChevronIconAnimation } = props;
  const linkColor = isLinkOpen ? activeStateColor : idleColor;
  const linkStyle = generateLinkStyleFromTheme({ idleColor: linkColor });
  const style = getMenuLinkStyle(baseUnit, linkColor);
  const doSubLinksExist = childrenLinks && childrenLinks.length !== 0;
  return (
    <React.Fragment>
      <li>
        <div className={css(style.style)}>
          <TextLinkComponent href={link.href} linkStyle={linkStyle}><H4>{link.text}</H4></TextLinkComponent>
          {doSubLinksExist ? (
            <div className={css(style.chevronIcon)} onClick={() => onClick(index)}>
              <CSSTransition
                in={startChevronIconAnimation}
                unmountOnExit
                mountOnEnter
                timeout={{
                  enter: chevronIconEnterTimeOut,
                  exit: chevronIconExitTimeOut,
                }}
                classNames={{
                  enter: css(style.enter),
                  enterActive: css(style.enterActive),
                  exit: css(style.exit),
                  exitActive: css(style.exitActive),
                }}
              >
                <ChevronIcon direction={isLinkOpen ? Directions.UP : Directions.DOWN} color={linkColor} />
              </CSSTransition>
            </div>
          ) : null}
        </div>
        {doSubLinksExist && isLinkOpen ? <MenuSubLinks links={childrenLinks!} isLinkOpen={isLinkOpen} /> : null}
      </li>
    </React.Fragment>
  );
};
